// @ts-strict-ignore

import { Subject, Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { Service } from 'rainbow-web-sdk/services/service';
import { $iq, $pres, Builder, Strophe } from 'strophe.js';
import { MaybeArray } from 'date-fns';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { StropheConnection, StropheHandler, StropheHandlerFn } from '../../libs/strophe/types';

export const XMPP_SVC = 'XmppService';

export class XmppService extends Service {

    private static REQUEST_TIMEOUT = 10000;
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private eventService: EventService = EventService.getInstance();

    private rxConnectionSubject = new Subject<any>();
    private rxContactSubject = new Subject<any>();

    private showBodyMessage: boolean = false;
    private presenceWaitingList: any[] = [];
    private xmppServerURL: string = null;
    private stropheStatus: number = null;
    private presenceStatus: string = "offline";
    private pingTimeout: number = null;
    private connecting: boolean = false;
    private disconnectInfo: any = null;
    private connectionWatcherTimout: any = null;
    private isInitialPresenceSent: boolean = false;

    private retrieveMessageHandler: any = null;
    private bulkHistoryHandler: any = null;

    private reconnectInProgress: boolean = false;
    private reconnectionDelay: number = 250;
    private reconnectTimeout: any | null = null;

    private connectivityEventSubscription: Subscription = null;
    private lastPingDate: any = null;

    public started: boolean = false;
    public connection: StropheConnection = null;
    public jid: string = null;
    public fullJid: string = null;
    public changePasswordInfo: any = null;
    public contactServiceReady = false;
    public connected: boolean = false;

    public reconnectionMode: boolean = false;

    public static getInstance() {
        return Service.create(XMPP_SVC, XmppService);
    }

    public static build() {
        return new XmppService();
    }

    protected constructor() {
        super();
    }

    private onHandleConnectivity(online: boolean) {
        if (online) {
            this.logger.info('[xmppService] onHandleConnectivityEvent -- retrieve connectivity');
            this.interruptAutoReconnect(true);
            this.autoReconnect();
        }
        else {
            if (this.stropheStatus === Strophe.Status.CONNECTED) {
                this.logger.info('[xmppService] onHandleConnectivityEvent -- lost connectivity');
                this.interruptAutoReconnect();
                this.disconnect('NO_NETWORK');
            }
        }
    }

    public async start() {
        try {
            this.connectivityEventSubscription = this.mainService.appConnectivity.subscribe((online: boolean) => { this.onHandleConnectivity(online); });

            // Retrieve and check credentials
            if (!this.authService.jidIm || !this.authService.jidPwd) { throw new Error("No valid XMPP credentials"); }

            // Compute serverURL
            this.xmppServerURL = `${config.xmpp.protocol}://${config.xmpp.currentServer}:${config.xmpp.port}/websocket`;

            // Read settings
            this.showBodyMessage = this.settingsService.getBooleanSetting('showBodyMessage');

            // Handle pingTimeout
            this.pingTimeout = config.xmpp.pingInterval ? 2 * parseInt(config.xmpp.pingInterval, 10) : 120000;

            // Connect to xmpp server
            await this.connect();
            this.publishConnectionEvent('ON_XMPP_CONNECTED', { reason: 'LOGIN' });
        }
        catch (error) {
            this.logger.error(`[xmppService] start -- FAILURE -- ${error.message}`);
            throw error;
        }
    }

    public async stop() {
        this.reconnectionMode = false;
        try { await this.disconnect('LOGOUT'); } catch (error) { /* Do nothing */ }
        this.started = false;
        this.connected = false;
        this.stropheStatus = null;
        this.presenceStatus = "offline";
        this.fullJid = null;
        this.lastPingDate = null;
        this.reconnectInProgress = false;
        if (this.connectivityEventSubscription) {
            this.connectivityEventSubscription.unsubscribe();
            this.connectivityEventSubscription = null;
        }
    }

    //API that is used by the SDK, to reset all variables & objects ...
    public resetAllObjects() {
        this.reconnectionMode = false;
        this.started = false;
        this.connected = false;
        this.stropheStatus = null;
        this.presenceStatus = "offline";
        this.fullJid = null;
        this.lastPingDate = null;
        if (this.connectivityEventSubscription) {
            this.connectivityEventSubscription.unsubscribe();
            this.connectivityEventSubscription = null;
        }
    }

    public async connect() {
        try {
            if (this.stropheStatus === Strophe.Status.CONNECTING || this.stropheStatus === Strophe.Status.CONNECTED || this.stropheStatus === Strophe.Status.AUTHENTICATING) {
                const error: any = new Error("XMPP service already connected or connecting");
                error.details = (this.stropheStatus === Strophe.Status.CONNECTING) ? "XMPP_IN_CONNECTING_STATE" : "XMPP_ALREADY_CONNECTED";
                throw error;
            }

            // Create the strophe connection object
            const client: string = this.authService.getInformationHeadersApp();
            const clientVersion: string = this.authService.getInformationHeadersAppVersion();
            const xmppDomain = this.authService.xmppDomain;
            if (client === null || clientVersion === null || xmppDomain === null) { throw new Error("XMPP service -- Missing connection data"); }

            //keep old rxsubject, as all services are listening to it; TO BE REWORKED WITH JINGLE CLASS
            let jingleRxSubject = null;
            if (this.connection?.jingle?.rxSubject) {
                jingleRxSubject = this.connection.jingle.rxSubject;
            }
            this.connection = new Strophe.Connection(`${this.xmppServerURL}?x-rainbow-client=${client}&x-rainbow-client-version=${clientVersion}&x-rainbow-xmpp-dom=${xmppDomain}`);
            this.connection.rawInput = (data: any) => { this.handleRawXmppMessage(data, "Recv: "); };
            this.connection.rawOutput = (data: any) => { this.handleRawXmppMessage(data, "Sent: "); };

            if (jingleRxSubject && this.connection && this.connection.jingle) {
                this.connection.jingle.rxSubject = jingleRxSubject;
            }
            // Generate the a full jid (with resource) if necessary
            this.jid = this.authService.jidIm;
            if (!this.fullJid) { this.fullJid = this.authService.getRandomFullJid(this.jid); }

            // Connect to xmpp server
            await this.stropheConnect();
            this.connected = true;

            // Start ping/pong mechanism
            this.connection.ping.addPingHandler((ping: any) => {
                this.logger.info(`[xmppService] onKeepAlivePingEvent (${new Date()})`);
                if (this.connected) {
                    this.connection.ping.pong(ping);
                    this.lastPingDate = new Date();
                    this.saveLastPingDate();
                }
                return true;
            });

            // Attach listeners
            this.addHandler((stanza: any) => { return this.onRosterChangedMessageReceived(stanza); }, "jabber:iq:roster", "iq", "set");
            this.addHandler((stanza: any) => { return this.onMamMessageReceived(stanza); }, Strophe.NS.MAM, "message", null);
            this.addHandler((stanza: any) => { return this.onMamMessageReceived(stanza); }, Strophe.NS.MAM, "iq", null);
            this.addHandler((stanza: any) => { return this.onMamBulkMessageReceived(stanza); }, "urn:xmpp:mam:1:bulk", "message", null);
            this.addHandler((stanza: any) => { return this.onMamBulkMessageReceived(stanza); }, "urn:xmpp:mam:1:bulk", "iq", null);
            this.addHandler((stanza: any) => { return this.onPresenceMessageReceived(stanza); }, null, "presence");

            // Reset Jingle handlers
            if (this.connection.jingle) { this.connection.jingle.attachHandlers(); }

            // Reset the presence waiting list
            this.presenceWaitingList = [];

            // Enable carbon
            const iq = $iq({ type: "set" });
            iq.c("enable", { xmlns: "urn:xmpp:carbons:2" });
            this.sendIQ(iq);

            // Publish connection event
            this.started = true;
        }
        catch (error) {
            this.logger.error(`[xmppService] connect -- FAILURE -- ${error.message}`);
            throw error;
        }
    }

    private saveLastPingDate() {
        localStorage.setItem("lastPingDate", this.lastPingDate);
    }

    public waitForGuestXmppReconnection(): Promise<void> {

        this.logger.info('[xmppService] waitForGuestXmppReconnection -- waiting');

        return new Promise((resolve) => {

            const xmppConnectionTimout = setTimeout(async () => {
                if (xmppConnectionTimout) clearTimeout(xmppConnectionTimout);
                if (xmppSubscription) { xmppSubscription.unsubscribe(); }
                this.logger.info('[xmppService] waitForGuestXmppReconnection -- security timeout');
                await this.stop();
                resolve();
            }, 15000);

            const xmppSubscription = this.subscribeToConnectionEvents(async () => {
                if (xmppConnectionTimout) clearTimeout(xmppConnectionTimout);
                if (xmppSubscription) { xmppSubscription.unsubscribe(); }
                this.logger.info('[xmppService] waitForGuestXmppReconnection -- reconnection done');
                await this.stop();
                resolve();
            });

            this.reconnectionMode = true;
            this.start();
        });
    }

    public disconnect(reason: string): Promise<any> {
        return new Promise((resolve) => {
            if (!this.connected) {
                this.logger.info(`[xmppService] disconnect - service is not started`);
                resolve(0);
            }
            else if (this.stropheStatus === Strophe.Status.CONNECTED) {
                this.logger.info(`[xmppService] disconnect -- ${reason}`);
                this.disconnectInfo = { reason, resolve };
                this.connection.disconnect(reason);
            }
            else {
                this.logger.warn(`[xmppService] disconnect - service is started and not started = flags are not correct`);
                // GFR : Avoid to be blocked on disconnect XMPP Service (seen with MCO)
                resolve(-1);
            }
        });
    }

    private stropheConnect() {
        return new Promise((resolve, reject) => {
            this.connecting = true;
            this.connection.connect(this.fullJid, this.authService.jidPwd, (status: number, reason: string = null, elem: any = null) => {
                this.stropheStatus = status;
                switch (status) {
                    case Strophe.Status.CONNECTING:
                        this.logger.info(`[xmppService] connecting -- ${this.fullJid}`);
                        this.publishConnectionEvent('ON_XMPP_CONNECTING');
                        break;
                    case Strophe.Status.AUTHFAIL:
                        this.logger.error(`[xmppService] onAuthenticationFailureEvent -- ${reason} -- ${this.fullJid}`);
                        this.connecting = false;
                        //this.reconnectInProgress = false; // MCO SDK TEST
                        //this.interruptAutoReconnect(true);// MCO SDK TEST
                        if (elem?.textContent === 'Invalid username or password') {
                            //On reconnect, if we receive 'Invalid username or password' then it means that we should stop trying to reconnect
                            // Use case: user's accompte is deleted/suspended
                            this.interruptAutoReconnect(true);
                            this.reconnectInProgress = true;
                            this.authService.logout();
                            //stop all services and go to the login page
                            this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: 'CHANGE_PWD_ERROR' });

                            break;
                        }
                        reject(new Error(`Authentication failure -- ${reason}`));
                        break;
                    case Strophe.Status.CONNFAIL:
                        if (!this.connecting) break;
                        this.logger.error(`[xmppService] onConnectionFailureEvent -- ConnectionFail -- ${this.fullJid}`);
                        this.connecting = false;
                        reject(new Error(`Connection failure -- ${reason}`));
                        break;
                    case Strophe.Status.CONNECTED:
                        this.logger.info(`[xmppService] onConnectedEvent -- ${this.fullJid}`);
                        this.connecting = false;
                        this.reconnectInProgress = false;
                        resolve(null);
                        break;
                    case Strophe.Status.DISCONNECTED:
                        if (this.connecting) {
                            this.logger.error(`[xmppService] onConnectionFailureEvent -- Disconnected -- ${this.fullJid}`);
                            reject(new Error(`Connection failure -- ${reason}`));
                            break;
                        }
                        if (!this.reconnectInProgress) { this.onDisconnectEvent(reason); }
                        break;
                    case Strophe.Status.ERROR:
                        if (this.connected) {
                            this.logger.error(`[xmppService] WebSocketError -- ${this.fullJid} -- ${reason}`);
                            if (reason === "resource-constraint") {
                                //we've reached the maximum number of connected devices, this one is "kicked"
                                //ignore disconnected event, if it's received just after
                                this.interruptAutoReconnect(true);
                                this.reconnectInProgress = true;
                                this.authService.logout();
                                //stop all services and go to the login page
                                this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: 'RESOURCE_CONSTRAINTS' });
                            }
                        }
                        break;
                    default: break;
                }
            });
        });
    }

    private connectionWatcher() {
        try {
            this.logger.error("[xmppService] connection watcher : so bad, no message since " + (this.pingTimeout / 1000) + " seconds (no ping)");
            if (this.connection) {
                const message = (this.stropheStatus === Strophe.Status.CONNECTED) ? "Have lost connectivity" : "Stuck in reconnecting state";
                this.logger.info(`[xmppService] connection watcher -- ${message}`);
                this.stropheStatus = null;
                this.connection.disconnect('No ping');
            }
        } catch (error) {
            this.logger.error("connectionWatcher error " + error);
        }
    }

    private handleRawXmppMessage(data: any, direction: string) {
        if (this.connectionWatcherTimout) { clearTimeout(this.connectionWatcherTimout); }
        this.connectionWatcherTimout = setTimeout(() => { this.connectionWatcher(); }, this.pingTimeout);

        // Log XMPP message (except photo stanza)
        if (!(data.indexOf("<PHOTO>") > -1)) {
            let log = direction;

            if (this.settingsService.getSetting("devMode") === "true" && this.settingsService.getSetting("showBodyMessage") === "true") {
                //if both flags are true, show all logs
                log += data;
                this.logger.debug(log);
                return;
            }

            if (data.indexOf("urn:xmpp:mam:1:bulk") > -1) {
                log += "Hidden content -- bulk";
            }
            else if (data.indexOf("xmlns='jabber:x:bubble:conference'") > -1 && (data.indexOf("name='invitation'") > -1 || data.indexOf("name='conferenceAdd'") > -1 || data.indexOf("name='conferenceRemove'") > -1)) {
                log += "Hidden content -- xmlns='jabber:x:bubble:conference -- ";
                if (data.indexOf("name='invitation'") > -1) log += " invitation";
                if (data.indexOf("name='conferenceAdd'") > -1) log += " conferenceAdd";
                if (data.indexOf("name='conferenceRemove'") > -1) log += " conferenceRemove";
            }
            else if (data.indexOf("urn:xmpp:jingle") > -1) {
                if (this.settingsService.getSetting("devMode") === "true"
                    && this.settingsService.getSetting("showBodyMessage") === "true") log += data;
                else if (data.indexOf("displayname") > -1 || data.indexOf("number") > -1) {
                    log += "Hidden content -- jingle";
                }
                else {
                    //hide local and remote IP addr
                    if (data.indexOf("<candidate") !== -1) {
                        //find IP addr
                        //take the whole line of candidate
                        const endOfCandidate = data.indexOf("/>", data.indexOf("<candidate"));
                        if (endOfCandidate !== -1) {
                            let line = data.substring(data.indexOf("<candidate"), endOfCandidate);

                            //ip v4
                            if (line.indexOf("relay") === -1) {
                                //replace all
                                line = line.replaceAll(/([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)[0-9]{1,3}/g, "$1" + "x");
                            }
                            else {
                                line = line.replace(/([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)[0-9]{1,3}/, "$1" + "x");
                            }

                            //ip v6
                            line = line.replace(/(([0-9a-z]{0,4}\:){1,4})([0-9a-z]{1,4}\:?){4}/, "$1" + "x:x:x:x");

                            data = data.substring(0, data.indexOf("<candidate")) + line + data.substring(endOfCandidate);
                        }
                    }
                    log += data;
                }
            }
            else if (data.indexOf("urn:xmpp:janus:1") > -1 && (data.indexOf("displayName") > -1 || data.indexOf("displayname") > -1 || data.indexOf("number") > -1)) {
                log += "Hidden content";
            }
            else if (data.indexOf("urn:xmpp:json:0") > -1) {
                log += "Hidden content";
            }
            else {
                if (data.indexOf("<message") > -1 && (data.indexOf("<body") > -1 || data.indexOf("<content>") > -1 || data.indexOf("<thumbnail") > -1)) { log += this.messageBodyDiscretion(data); }
                else {
                    let result = data;
                    const dataElem = xmlParser(data).find('data');
                    if (dataElem.attr("xmlns") === "http://jabber.org/protocol/ibb") { log += "Encoded file in base 64"; }

                    // Hide identity info from xmpp messages
                    if (!this.showBodyMessage) {
                        if (data.indexOf("<identity") !== -1 || data.indexOf("<endpoint") !== -1) {
                            const firstName = data.indexOf("firstName=");
                            const firstNameIndex = (data.indexOf("'", firstName) !== -1) ? data.indexOf("'", firstName) : data.indexOf('"', firstName);
                            if (firstName !== -1 && firstNameIndex !== -1) {
                                const firstNameLastIndex = (data.indexOf("'", firstNameIndex + 1) !== -1) ? data.indexOf("'", firstNameIndex + 1) : data.indexOf('"', firstNameIndex + 1);
                                if (firstNameIndex + 1 !== firstNameLastIndex) {
                                    result = data.substr(0, firstNameIndex + 2) + "***" + data.substr(firstNameLastIndex);
                                }
                            }

                            const lastName = result.indexOf("lastName=");
                            const lastNameIndex = (result.indexOf("'", lastName) !== -1) ? result.indexOf("'", lastName) : result.indexOf('"', lastName);
                            if (lastName !== -1 && lastNameIndex !== -1) {
                                const lastNameLastIndex = (result.indexOf("'", lastNameIndex + 1) !== -1) ? result.indexOf("'", lastNameIndex + 1) : result.indexOf('"', lastNameIndex + 1);
                                if (lastNameIndex + 1 !== lastNameLastIndex) {
                                    result = result.substr(0, lastNameIndex + 2) + "***" + result.substr(lastNameLastIndex);
                                }
                            }

                            const displayName = result.indexOf("displayName=");
                            const displayNameIndex = (result.indexOf("'", displayName) !== -1) ? result.indexOf("'", displayName) : result.indexOf('"', displayName);
                            if (displayName !== -1 && displayNameIndex !== -1) {
                                const displayNameLastIndex = (result.indexOf("'", displayNameIndex + 1) !== -1) ? result.indexOf("'", displayNameIndex + 1) : result.indexOf('"', displayNameIndex + 1);
                                if (displayNameIndex + 1 !== displayNameLastIndex) {
                                    result = result.substr(0, displayNameIndex + 2) + "***" + result.substr(displayNameLastIndex);
                                }
                            }
                        }

                        // Hide phone number info from xmpp messages
                        if (result.indexOf("<caller_info") !== -1 || result.indexOf("<callee_info") !== -1) {
                            let number = result.indexOf("number=");
                            let numberIndex = result.indexOf("'", number);

                            if (number !== -1 && numberIndex !== -1) {
                                const numberLastIndex = result.indexOf("'", numberIndex + 1);
                                if (numberIndex + 1 !== numberLastIndex) {
                                    const numberText = result.substr(numberIndex + 1, numberLastIndex - numberIndex - 1);
                                    result = result.substr(0, numberIndex + 1) + anonymizer(numberText) + result.substr(numberLastIndex);
                                }
                            }

                            number = result.indexOf("calledNumber=");
                            numberIndex = result.indexOf("'", number);

                            if (number !== -1 && numberIndex !== -1) {
                                const numberLastIndex = result.indexOf("'", numberIndex + 1);
                                if (numberIndex + 1 !== numberLastIndex) {
                                    const numberText = result.substr(numberIndex + 1, numberLastIndex - numberIndex - 1);
                                    result = result.substr(0, numberIndex + 1) + anonymizer(numberText) + result.substr(numberLastIndex);
                                }
                            }
                        }

                        if (result.indexOf("<caller>") !== -1) {
                            const caller = result.indexOf("<caller>");
                            const endCaller = result.indexOf("</caller>", caller);
                            const numberText = result.substring(caller + 8, endCaller);
                            if (numberText.indexOf("@") === -1 && numberText.indexOf("janus") === -1) {
                                result = result.substr(0, caller + 8) + anonymizer(numberText) + result.substr(endCaller);
                            }
                        }

                        if (result.indexOf("<lastMessageText>") !== -1) {
                            const lastMessage = result.indexOf("<lastMessageText>");
                            const endLastMessage = result.indexOf("</lastMessageText>", lastMessage);
                            const numberText = result.substring(lastMessage + 17, endLastMessage);
                            result = result.substr(0, lastMessage + 13) + anonymizer(numberText) + result.substr(endLastMessage);
                        }

                        if (result.indexOf("<callee>") !== -1) {
                            const callee = result.indexOf("<callee>");
                            const endCallee = result.indexOf("</callee>", callee);
                            const numberText = result.substring(callee + 8, endCallee);
                            if (numberText.indexOf("@") === -1 && numberText.indexOf("janus") === -1) {
                                result = result.substr(0, callee + 8) + anonymizer(numberText) + result.substr(endCallee);
                            }
                        }

                        //Telephony messages things
                        const endpointTel = result.indexOf("endpointTel='");
                        if (endpointTel !== -1) {
                            const endEndpointTel = result.indexOf("'", endpointTel + 13);
                            const numberText = result.substring(endpointTel + 13, endEndpointTel);
                            result = result.substr(0, endpointTel + 13) + anonymizer(numberText) + result.substr(endEndpointTel);
                        }

                        const destination = result.indexOf("destination='");
                        if (destination !== -1) {
                            const endDestination = result.indexOf("'", destination + 13);
                            const numberText = result.substring(destination + 13, endDestination);
                            result = result.substr(0, destination + 13) + anonymizer(numberText) + result.substr(endDestination);
                        }

                        if (result.indexOf("<name>") !== -1) {
                            const message = result.indexOf("<name>");
                            const endMessage = result.indexOf("</name>", message);
                            result = result.substr(0, message + 6) + "***" + result.substr(endMessage);
                        }

                        if (result.indexOf("urn:xmpp:pbxagent:telephony") !== -1) { // Sipwise telephony events
                            result = this.anonymizeTag(result, "phoneNumber");
                            result = this.anonymizeTag(result, "displayName");
                            result = this.anonymizeTag(result, "firstName");
                            result = this.anonymizeTag(result, "lastName");
                        }

                        if (result.indexOf("urn:xmpp:client:supervision:1") !== -1) { // supervision events
                            result = this.anonymizeAttribut(result, "phoneNumber");
                            result = this.anonymizeAttribut(result, "displayName");
                            result = this.anonymizeAttribut(result, "firstName");
                            result = this.anonymizeAttribut(result, "lastName");
                        }

                        // Hide filename if found
                        if (result.indexOf("<filename>") !== -1) {
                            result = this.messageTagDiscretion("filename", result);
                        }
                    }

                    log += result;
                }
            }
            this.logger.debug(log);
        }
    }

    private anonymizeTag(message: string, tag: string): string {
        if (!message || !tag) return message;
        const xmlTag: string = "<" + tag + ">";
        const xmlEndTag: string = "</" + tag + ">";

        let currentIndex: number = 0;
        let tagIndex: number;
        do {
            tagIndex = message.indexOf(xmlTag, currentIndex);
            if (tagIndex !== -1) {
                const endTagIndex: number = message.indexOf(xmlEndTag, tagIndex + xmlTag.length);
                const tagContent: string = message.substring(tagIndex + xmlTag.length, endTagIndex);
                message = message.substring(0, tagIndex + xmlTag.length) + anonymizer(tagContent) + message.substring(endTagIndex);
                currentIndex = endTagIndex + xmlEndTag.length;
            }
        } while (tagIndex !== -1);
        return message;
    }

    private anonymizeAttribut(data: string, tag: string): string {
        let iteration = 0;
        let index = 0;
        let startTagIndex = 0;
        do {
            startTagIndex = data.indexOf(tag + "=", index);
            if (startTagIndex !== -1) {
                const endTagIndex = (data.indexOf("'", startTagIndex) !== -1) ? data.indexOf("'", startTagIndex) : data.indexOf('"', startTagIndex);
                if (endTagIndex !== -1) {
                    const endTagLastIndex = (data.indexOf("'", endTagIndex + 1) !== -1) ? data.indexOf("'", endTagIndex + 1) : data.indexOf('"', endTagIndex + 1);
                    if (endTagIndex + 1 !== endTagLastIndex) {
                        data = data.substr(0, endTagIndex + 2) + "***" + data.substr(endTagLastIndex);
                    }
                }

                index = endTagIndex + 1;
            }
            iteration += 1;

            if (iteration > 4) {
                //exit while cycle in case of infinite ... just in case
                startTagIndex = -1;
            }
        } while (startTagIndex !== -1);

        return data;
    }

    private messageBodyDiscretion(data: string): string {
        if (this.showBodyMessage) { return data; }
        data = this.messageTagDiscretion("content", data);
        data = this.messageTagDiscretion("body", data);
        data = this.messageTagDiscretion("subject", data);
        data = this.messageTagDiscretion("filename", data);
        return data;
    }

    private messageTagDiscretion(tag: string, data: string): string {
        let iteration = 0;
        let index = 0;
        let startTagIndex = 0;
        do {
            startTagIndex = data.indexOf("<" + tag, index);
            if (startTagIndex !== -1) {
                const endTagIndex = data.indexOf(">", startTagIndex);
                const endEmptyTagIndex = data.indexOf("/>", startTagIndex);
                if (endTagIndex !== -1 && endTagIndex !== endEmptyTagIndex + 1) {
                    data = data.substr(0, endTagIndex + 2) + "***" + data.substr(data.indexOf("</" + tag + ">", startTagIndex) - 1);
                }

                index = endTagIndex + 1;
            }
            iteration += 1;

            if (iteration > 4) {
                //exit while cycle in case of infinite ... just in case
                startTagIndex = -1;
            }
        } while (startTagIndex !== -1);

        return data;
    }

    private async onDisconnectEvent(reason: string) {

        // Remove the connectionWatcher (if any)
        if (this.connectionWatcherTimout) { clearTimeout(this.connectionWatcherTimout); }
        this.authService.stopTokenSurvey();

        // Reset presenceSent status
        this.isInitialPresenceSent = false;

        // If service is not started ignore this event
        if (!this.started) { this.logger.info("[xmppService] onDisconnectEvent -- service not started -- ignored"); return; }

        // Handle expected disconnection
        if (this.disconnectInfo) {
            this.logger.info(`[xmppService] onDisconnectEvent -- Expected disconnection -- ${this.disconnectInfo.reason} -- ${this.fullJid}`);
            this.connected = false;
            this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: this.disconnectInfo.reason });
            this.disconnectInfo.resolve();
            this.disconnectInfo = null;
            return;
        }

        // Handle disconnection due to change password
        if (this.changePasswordInfo) {
            this.connected = false;
            if (this.changePasswordInfo.fromThirdParty) {
                this.logger.error(`[xmppService] onDisconnectEvent -- Expected disconnection -- change password from third party -- ${this.fullJid}`);
                this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: '3RD_PARTY_CHANGE_PWD' });
            }
            else {
                this.logger.error(`[xmppService] onDisconnectEvent -- Expected disconnection -- change password -- ${this.fullJid}`);
                const hasTokenBeenStored = Boolean(localStorage.getItem("token"));
                try { await this.authService.logon(this.changePasswordInfo.login, this.changePasswordInfo.newPassword, hasTokenBeenStored); }
                catch (error) { this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: 'CHANGE_PWD_ERROR' }); }
                if (this.changePasswordInfo.resolve) { this.changePasswordInfo.resolve(); }
                this.changePasswordInfo = null;
                this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: 'CHANGE_PWD' });
                this.interruptAutoReconnect();
                this.autoReconnect();
            }
            return;
        }

        // Handle unexpected disconnection (try to reconnect)
        this.logger.error(`[xmppService] onDisconnectEvent -- Unexpected disconnection -- ${reason} -- ${this.fullJid}`);
        this.connected = false;
        this.publishConnectionEvent('ON_XMPP_DISCONNECTED', { reason: 'UNKNOWN' });
        if (!this.reconnectInProgress) { this.autoReconnect(); }
        else { this.logger.error(`[xmppService] onDisconnectEvent -- Unexpected disconnection -- reconnection already in progress`); }
    }

    private interruptAutoReconnect(force: boolean = false) {
        if (this.reconnectInProgress || force) {
            this.logger.info("[xmppService] interrupt auto-reconnection");

            // Re-init the connection mechanism
            this.reconnectionDelay = 250;
            this.reconnectInProgress = false;

            // Remove reconnection timeout
            if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); }

            // Remove connection watcher
            if (this.connectionWatcherTimout) {
                this.logger.info("[xmppService] Clear connection watcher timeout");
                clearTimeout(this.connectionWatcherTimout);
            }
        }
    }

    public async autoReconnect() {
        try {
            this.logger.info("[xmppService] autoReconnect");
            this.reconnectInProgress = true;
            await this.authService.startTokenSurvey();
            await this.connect();
            this.reconnectionDelay = 250;
            this.reconnectInProgress = false;
            this.logger.info("[xmppService] autoReconnect -- SUCCESS");
            this.publishConnectionEvent('ON_XMPP_CONNECTED', { reason: 'RECONNECTION', lastActivityDate: this.lastPingDate });
        }
        catch (errorInfo) {
            let error = errorInfo;
            if (!errorInfo?.details) { error = new Error(); error.details = "UNKNOWN"; }

            switch (error.details) {
                case "AUTH_TOKEN_EXPIRED":
                    this.logger.error("[xmppService] Impossible to renew token, redirect to login page");
                    this.interruptAutoReconnect();
                    this.authService.logout();
                    ///this.stopServices();
                    //navigationService.displayLogin("tokenRenewImpossible"); //MCO
                    this.reconnectInProgress = false;
                    break;
                case "XMPP_ALREADY_CONNECTED":
                    this.logger.error("[xmppService] In auto-reconnect, but already connected -- ignore");
                    this.reconnectInProgress = false;
                    break;
                case "XMPP_IN_CONNECTING_STATE":
                    this.logger.error("[xmppService] xmpp is still connecting -- wait");
                    break;
                default:
                    if (this.reconnectInProgress) {
                        this.reconnectionDelay = 2 * this.reconnectionDelay;
                        // add random delay between 0 and the current value (in ms)
                        //we can go up to 16 sec + 32 sec in the worst case scenario
                        //we want to keep the most diverse delay to avoid "pic" in the reconnection
                        const randomNumber = Math.floor(Math.random() * this.reconnectionDelay);
                        if (this.reconnectionDelay > 16000) { this.reconnectionDelay = 16000; }
                        this.logger.info("[xmppService] Auto-reconnection failure (next try in " + (this.reconnectionDelay + randomNumber) + "ms)");
                        this.reconnectTimeout = setTimeout(() => { this.autoReconnect(); }, this.reconnectionDelay + randomNumber);
                    }
                    // this.reconnectInProgress = false;
                    break;
            }
        }
    }

    /* *********************************************************** */
    /* ** EVENTS STUFF                                          ** */
    /* *********************************************************** */
    public subscribeToContactEvents(observer: any): Subscription { return this.rxContactSubject.subscribe(observer); }
    public subscribeToConnectionEvents(observer: any): Subscription { return this.rxConnectionSubject.subscribe(observer); }
    public publishContactEvent(name: string, params: any) { this.rxContactSubject.next(RBEvent.create(name, params)); }

    public publishConnectionEvent(name: string, data: any = null) {
        if (!this.reconnectionMode || (name === 'ON_XMPP_CONNECTED' && (data.reason === 'RECONNECTION' || data.reason === 'LOGIN'))) {
            if (name === 'ON_XMPP_CONNECTED') this.mainService.appConnected.next(true);
            else if (name === 'ON_XMPP_DISCONNECTED') {
                // In password change cases we are disconnected but information banner should not be displayed
                if (data.reason === '3RD_PARTY_CHANGE_PWD' || data.reason === 'CHANGE_PWD_ERROR' || data.reason === 'RESOURCE_CONSTRAINTS') this.mainService.appConnected.next(true);
                else this.mainService.appConnected.next(false);
            }
            this.rxConnectionSubject.next(RBEvent.create(name, data));
        }
    }

    /* *********************************************************** */
    /* ** ROSTER STUFF                                          ** */
    /* *********************************************************** */
    private onRosterChangedMessageReceived(stanza: any): boolean {
        try {
            xmlParser(stanza).find("item").forEach((elem: XMLElement) => {
                const jid = this.getBareJidFromJid(elem.attr("jid"));
                const favorite = elem.find("group").first() ? (elem.find("group").first().text() === "favorites") : false;
                const sub = elem.attr("subscription");
                const ask = elem.attr("ask");
                this.publishContactEvent('ON_ROSTER_CHANGED_EVENT', { jid, subscription: (sub ? sub : "none"), favorite, ask: (ask ? ask : "none") });
            });
            return true;
        }
        catch (error) { return true; }
    }

    /* *********************************************************** */
    /* ** PRESENCE STUFF                                          ** */
    /* *********************************************************** */
    public sendPresence(show: string, message?: string, auth: boolean = false, userOptions?: { im: boolean; alert: boolean; companyid: string; ringingOnDnd: boolean; rejectOnBusy: boolean; }) {
        if (!this.connection?.send || this.stropheStatus !== Strophe.Status.CONNECTED) {
            this.logger.error('[xmppService] Try to send "presence IQ" but no xmpp connection');
            return;
        }

        try {
            this.logger.info("Send presence " + show + " - " + message + " (" + this.jid + ")");
            const presenceIq = $pres();
            presenceIq.c("priority").t("5");
            presenceIq.up();

            if (show && show !== "online") { presenceIq.c("show").t(show); }
            if (message && (!show || show === "online")) { presenceIq.c("status").t(message); }
            else if (message) { presenceIq.up().c("status").t(message); }

            // Add authentication information
            if (auth) {
                //add default values for user options, just in case
                if (!userOptions) {
                    userOptions = { im: true, alert: false, companyid: "", ringingOnDnd: true, rejectOnBusy: true };
                }
                if (show && show !== "online") { presenceIq.up(); } //close the "show" section
                presenceIq.c("application", { xmlns: "jabber:iq:application" });
                presenceIq.c("appid").t(config.rainbowApiClientKey).up();
                presenceIq.c("xrbversion").t(version).up();
                presenceIq.c("xrbclient").t(this.authService.getInformationHeadersApp()).up();
                presenceIq.c("companyid").t(userOptions.companyid).up();
                presenceIq.c("userid").t(this.authService.userId).up().up();
                presenceIq.c("parameters", { xmlns: "jabber:iq:configuration" });
                presenceIq.c("user_options", {
                    alert: userOptions.alert.toString(),
                    im: userOptions.im.toString(),
                    ringingOnDnd: userOptions.ringingOnDnd.toString(),
                    rejectOnBusy: userOptions.rejectOnBusy.toString()
                });
            }
            this.connection.send(presenceIq);
        }
        catch (error) {
            this.logger.error("this.connection.send error : " + error);
            return;
        }
    }

    public resetPresence(status: any) {
        this.logger.info(`Reset presence to ${this.presenceStatus} (${this.jid})`);
        this.sendPresence(status.show, status.status);
    }

    private onPresenceMessageReceived(presence: any): boolean {
        try {
            // Extract info from stanza
            const pres = xmlParser(presence);
            const fromJid = pres.attr("from");
            const fromBareJid = this.getBareJidFromJid(fromJid);
            const namespace = pres.find("x").attr("xmlns");
            const ptype = pres.attr("type");

            //RQRAINB-11550++
            if (this.fullJid === fromJid) {
                const vcardCusto = pres.find("x").attr("xmlns") === "vcard-temp:x:update";
                if (vcardCusto && pres.find("custo").length > 0) {
                    this.logger.debug('[xmppService] ON_MYCUSTO_UPDATE_EVENT sent');
                    this.eventService.publish("ON_MYCUSTO_UPDATE_EVENT");
                }
            }//RQRAINB-11550--

            // Ignore muc presence
            if (namespace && namespace.indexOf(Strophe.NS.MUC) === 0) { return true; }

            if (pres.find("actor").length > 0 && pres.find("actor").attr("xmlns") === "jabber:iq:configuration") {
                if (pres.find("avatar").length > 0) { this.publishContactEvent('ON_CONTACT_UPDATE_EVENT', { from: fromBareJid, type: "avatar" }); }
                else if (pres.find("data").length > 0) { this.publishContactEvent('ON_CONTACT_UPDATE_EVENT', { from: fromBareJid, type: "data" }); }
                if (pres.find("x").length && pres.find("x").attr("xmlns") === "vcard-temp:x:update" && !pres.find("delay").length) { return true; }
            }

            // Handle custom status event for roster contacts
            const customStatusElem = pres.find('customStatus');
            if (customStatusElem?.length) {
                const contentElem = customStatusElem.find('content');
                if (contentElem?.length) {
                    // creation or update of custom status
                    this.logger.info("[xmppService] onPresenceMessageReceived - customStatus created");
                    const eventAttr = { jid: fromJid, customStatus: contentElem?.text() };
                    if (this.contactServiceReady) { this.publishContactEvent('ON_CONTACT_CUSTOM_STATUS_UPDATE_EVENT', eventAttr); }
                } else {
                    // deletion of custom status
                    this.logger.info("[xmppService] onPresenceMessageReceived - customStatus deleted");
                    const eventAttr = { jid: fromJid };
                    if (this.contactServiceReady) { this.publishContactEvent('ON_CONTACT_CUSTOM_STATUS_DELETE_EVENT', eventAttr); }
                }
                return true;
            }

            // Handle error message
            if (ptype === "error") { this.logger.error("Receive error presence message"); }

            // Handle new roster subscription
            else if (ptype === "subscribe") { this.logger.info("Receive subscribe from (" + fromBareJid + ")"); }

            // Handle contact presence
            else {
                // Handle status and message
                let presenceStatus = "offline";
                let presenceMessage = "";
                if (ptype !== "unavailable") {
                    const show = pres.find("show").text();
                    presenceMessage = pres.find("status").text();
                    if (!show || show === "chat") { presenceStatus = "online"; }
                    else if (show === "dnd") { presenceStatus = "dnd"; }
                    else if (show === "xa") { presenceStatus = "xa"; }
                    else { presenceStatus = "away"; }
                }

                const applyCalendarPresence = Boolean(pres.find("applyCalendarPresence").length);

                // Handle presenceStamp parameter
                const stamp = pres.find("delay").attr("stamp");
                const presStamp = stamp ? new Date(stamp) : new Date();

                // Handle until parameter
                let until: any = pres.find("until").text();
                if (until) { until = new Date(until); }
                else { until = null; }

                // Send rosters updated event
                const eventAttr = { jid: fromJid, status: presenceStatus, message: presenceMessage, stamp: presStamp, until: until, applyCalendarPresence: applyCalendarPresence };

                //
                if (this.isConnectedJid(fromJid) && !this.isInitialPresenceSent) {
                    this.isInitialPresenceSent = true;
                    this.publishConnectionEvent('ON_XMPP_CONNECTED', { reason: 'PRESENCE' });
                }

                if (this.contactServiceReady) { this.publishContactEvent('ON_CONTACT_PRESENCE_EVENT', eventAttr); }
                else {
                    this.logger.info('[xmppService] Receive presence message but contact service is not ready yet');
                    this.presenceWaitingList.push(eventAttr);
                }
            }

            // Keep the presence handler alive
            return true;
        }
        catch (error) { return true; }
    }

    public getPresenceWaitingList() { return this.presenceWaitingList; }
    public resetPresenceWaitingList() { this.presenceWaitingList = []; }

    /* *********************************************************** */
    /* ** MAM STUFF                                             ** */
    /* *********************************************************** */
    public addRetrieveMessageHandler(handler: any) { this.retrieveMessageHandler = handler; }
    public addBulkHistoryHandler(handler: any) { this.bulkHistoryHandler = handler; }

    public onMamBulkMessageReceived(stanza) {
        try { this.bulkHistoryHandler(stanza); } catch (error) { return true; }
        return true;
    }

    private onMamMessageReceived(stanza: any): boolean {
        try {
            // Get queryId and deleteId
            const stanzaElem = xmlParser(stanza);
            let queryId = stanzaElem.find("result").attr("queryid");
            if (!queryId) { queryId = stanzaElem.attr("id"); }

            // jidIm are used for history
            if (queryId && (queryId.indexOf("tel_") !== -1 || queryId.indexOf("cache_") !== -1) && this.retrieveMessageHandler) {
                this.retrieveMessageHandler(stanza);
            }

            return true;
        }
        catch (error) { return true; }
    }

    /* *********************************************************** */
    /* ** XMPP HELPERS                                          ** */
    /* *********************************************************** */
    public getBareJidFromJid(jid: string): string { return Strophe.getBareJidFromJid(jid); }
    public getResourceFromJid(jid: string): string { return Strophe.getResourceFromJid(jid); }
    public isMobileResource(jid: string): boolean { return jid.indexOf("mobile_") !== -1; }
    public addHandler(handler: StropheHandlerFn, ns: string, name: string = null, type: string | string[] = null): StropheHandler { return this.connection.addHandler(handler, ns, name, type); }
    public deleteHandler(handRef: StropheHandler): void { if (this.connection) { this.connection.deleteHandler(handRef); } }
    public isConnectedJid(jid: string): boolean { if (!jid || jid.length === 0) { return false; } return jid.startsWith(this.jid); }

    public send(stanza: MaybeArray<Builder> | MaybeArray<Element>): Promise<void> {
        return new Promise<void>((resolve) => {
            try {
                if (!this.connection && this.stropheStatus !== Strophe.Status.CONNECTED) {
                    this.logger.error('[xmppService] Try to send "stanza" but no xmpp connection');
                    throw new Error("No XMPP connection");
                }
                this.connection.send(stanza);
            }
            catch (error) { this.logger.error("[xmppService] this.connection.send error : " + error); }
            resolve();
        });
    }

    public sendIQ(iq: Builder | Element, timeout: number = null): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                if (!this.connection && this.stropheStatus !== Strophe.Status.CONNECTED) {
                    this.logger.error('[xmppService] Try to send "iq" but no xmpp connection');
                    throw new Error('No XMPP connection');
                }
                this.connection.sendIQ(
                    iq,
                    (data: any) => { resolve(data); },
                    (error: any) => { reject(new Error(error ? error.innerHTML : 'XMPP request timeout')); },
                    (timeout && (timeout > XmppService.REQUEST_TIMEOUT)) ? timeout : XmppService.REQUEST_TIMEOUT
                );
            }
            catch (error) {
                this.logger.error(`[xmppService] this.connection.sendIQ error : ${error}`);
                reject(new Error('this.connection.sendIQ error'));
            }
        });
    }
}
